import { ConnectedPosition, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  Component,
  contentChild,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, fromEvent } from 'rxjs';

import { SideNavDropdownPanelDirective } from './directives/side-nav-dropdown-panel.directive';
import { SideNavDropdownTriggerDirective } from './directives/side-nav-dropdown-trigger.directive';

const DEFAULT_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 20, offsetY: 0 },
  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12, offsetY: 0 },
  { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -12, offsetY: 0 },
  { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -12, offsetY: 0 },
];

@Component({
  selector: 'crm-tgbot-side-nav-dropdown',
  imports: [OverlayModule, PortalModule],
  exportAs: 'crmTgbotSideNavDropdown',
  templateUrl: './side-nav-dropdown.component.html',
})
export class SideNavDropdownComponent {
  private overlay = inject(Overlay);
  private vcr = inject(ViewContainerRef);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  offsetX = input(12);
  offsetY = input(0);
  positions = input<ConnectedPosition[]>(DEFAULT_POSITIONS);
  closeOnNavigate = input(true, {
    transform: booleanAttribute,
  });
  opened = output<void>();
  closed = output<void>();

  open = model(false);
  trigger = contentChild(SideNavDropdownTriggerDirective);
  panel = contentChild(SideNavDropdownPanelDirective);

  private overlayRef?: OverlayRef;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      if (isOpen && !this.overlayRef) this.openOverlay();
      if (!isOpen && this.overlayRef) this.closeOverlay();
    });

    effect(() => {
      const trig = this.trigger();
      const pnl = this.panel();
      if (!trig || !pnl) return;

      fromEvent(trig.el.nativeElement, 'click')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e: Event) => {
          e.stopPropagation();
          this.open.update((v) => !v);
        });
    });

    effect(() => {
      if (!this.closeOnNavigate()) return;
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.open.set(false));
    });
  }

  private openOverlay() {
    const trig = this.trigger();
    const pnl = this.panel();
    if (!trig || !pnl) return;

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(trig.el)
      .withPositions(this.positions())
      .withDefaultOffsetX(this.offsetX())
      .withDefaultOffsetY(this.offsetY())
      .withPush(true)
      .withFlexibleDimensions(false)
      .withGrowAfterOpen(false);

    this.overlayRef = this.overlay.create({
      positionStrategy: strategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'crm-tgbot-dropdown-panel',
    });

    this.overlayRef.attach(new TemplatePortal(pnl.tpl, this.vcr));
    this.opened.emit();

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));

    this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ev) => {
        if (ev.key === 'Escape') this.open.set(false);
      });

    this.overlayRef
      .detachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cleanupOverlay());
  }

  private closeOverlay() {
    this.overlayRef?.detach();
  }

  private cleanupOverlay() {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.closed.emit();
  }
}
